import { useState, useEffect } from 'react';
import { Clock, Plus, Trash2, Save } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface CountdownTimer {
  id?: number;
  name: string;
  message: string;
  end_time: string;
  is_active: boolean;
  product_id: number | null;
  category_id: number | null;
}

export default function UpsellTimers() {
  const [timers, setTimers] = useState<CountdownTimer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state for new timer
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTimer, setNewTimer] = useState<Partial<CountdownTimer>>({
    name: '',
    message: '',
    end_time: '',
    is_active: true,
    product_id: null,
    category_id: null,
  });

  useEffect(() => {
    fetchTimers();
  }, []);

  async function fetchTimers() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ timers: CountdownTimer[] }>('/admin/timers');
      setTimers(response.timers || []);
    } catch (err) {
      setError('Failed to load countdown timers. Please try again.');
      console.error('Error fetching timers:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleAddTimer(e: React.FormEvent) {
    e.preventDefault();
    setSuccessMessage(null);

    if (!newTimer.name || !newTimer.message || !newTimer.end_time) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      await api.post('/admin/timers', newTimer);
      setSuccessMessage('Countdown timer added successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setShowAddForm(false);
      setNewTimer({
        name: '',
        message: '',
        end_time: '',
        is_active: true,
        product_id: null,
        category_id: null,
      });
      await fetchTimers();
    } catch (err) {
      setError('Failed to add timer. Please try again.');
      console.error('Error adding timer:', err);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleActive(id: number, currentState: boolean) {
    try {
      setError(null);
      await api.patch(`/admin/timers/${id}`, {
        is_active: !currentState,
      });
      setSuccessMessage(`Timer ${!currentState ? 'activated' : 'deactivated'} successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
      setTimers(prev => prev.map(t => t.id === id ? { ...t, is_active: !currentState } : t));
    } catch (err) {
      setError('Failed to update timer. Please try again.');
      console.error('Error updating timer:', err);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm('Are you sure you want to delete this countdown timer?')) {
      return;
    }

    try {
      setError(null);
      await api.delete(`/admin/timers/${id}`);
      setSuccessMessage('Countdown timer deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setTimers(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      setError('Failed to delete timer. Please try again.');
      console.error('Error deleting timer:', err);
    }
  }

  function formatDateTime(dateStr: string) {
    return new Date(dateStr).toLocaleString();
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading countdown timers...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-8 h-8 text-gray-700" aria-hidden="true" />
              <h1 className="text-3xl font-bold text-gray-900">Countdown Timers</h1>
            </div>
            <p className="text-gray-600">Create urgency with limited-time offers</p>
          </div>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
            Add Timer
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" role="status" aria-live="polite">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {showAddForm && (
        <Card title="Add New Countdown Timer" padding="lg" className="mb-6">
          <form onSubmit={handleAddTimer} className="space-y-4">
            <Input
              label="Timer Name"
              value={newTimer.name || ''}
              onChange={(e) => setNewTimer(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Flash Sale"
              required
            />

            <Textarea
              label="Display Message"
              value={newTimer.message || ''}
              onChange={(e) => setNewTimer(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Sale ends in..."
              rows={2}
              required
            />

            <Input
              label="End Date & Time"
              type="datetime-local"
              value={newTimer.end_time || ''}
              onChange={(e) => setNewTimer(prev => ({ ...prev, end_time: e.target.value }))}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Product ID (optional)"
                type="number"
                value={newTimer.product_id || ''}
                onChange={(e) => setNewTimer(prev => ({
                  ...prev,
                  product_id: e.target.value ? parseInt(e.target.value) : null,
                  category_id: null,
                }))}
                placeholder="123"
                min="1"
                helperText="Show timer on specific product"
              />
              <Input
                label="Category ID (optional)"
                type="number"
                value={newTimer.category_id || ''}
                onChange={(e) => setNewTimer(prev => ({
                  ...prev,
                  category_id: e.target.value ? parseInt(e.target.value) : null,
                  product_id: null,
                }))}
                placeholder="5"
                min="1"
                helperText="Show timer on category pages"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="timer_active"
                checked={newTimer.is_active || false}
                onChange={(e) => setNewTimer(prev => ({ ...prev, is_active: e.target.checked }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="timer_active" className="text-sm font-medium text-gray-700">
                Active
              </label>
            </div>

            <div className="flex gap-3">
              <Button type="submit" variant="primary" size="md" isLoading={isSaving}>
                <Save className="w-4 h-4 mr-2" aria-hidden="true" />
                Save Timer
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="md"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card title={`Active Timers (${timers.length})`} padding="lg">
        {timers.length === 0 ? (
          <p className="text-stone-500 text-center py-8">
            No countdown timers configured. Click "Add Timer" to create one.
          </p>
        ) : (
          <div className="space-y-4">
            {timers.map(timer => (
              <div
                key={timer.id}
                className="border border-stone-200 rounded-lg p-4 hover:border-stone-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-stone-900">{timer.name}</h3>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          timer.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-stone-100 text-stone-600'
                        }`}
                      >
                        {timer.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-stone-600 mb-2">{timer.message}</p>
                    <div className="flex gap-6 text-sm text-stone-500">
                      <span>Ends: {formatDateTime(timer.end_time)}</span>
                      {timer.product_id && <span>Product #{timer.product_id}</span>}
                      {timer.category_id && <span>Category #{timer.category_id}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(timer.id!, timer.is_active)}
                    >
                      {timer.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(timer.id!)}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
